import { jsx as _jsx } from "react/jsx-runtime";
import { Card, CardContent, CircularProgress, Stack } from '@mui/material';
import { useTimetableExistsQueryApi } from 'api';
import { LoadingWrapper } from 'components/custom';
import { useIsUntisUser } from 'components/kratos';
import { useCallback, useEffect, useRef } from 'react';
import CalendarForDashboard from './CalendarForDashboard';
import EmptyTimetable from './EmptyTimetable';
const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 30000;
const Timetable = () => {
    const isUntisUser = useIsUntisUser();
    const status = useTimetableExistsQueryApi();
    const { refetch } = status;
    const initialLoadDoneRef = useRef(false);
    const pollStartRef = useRef(null);
    // Track when initial GraphQL load completes (only once)
    if (!status.loading && !initialLoadDoneRef.current) {
        initialLoadDoneRef.current = true;
    }
    /*
     * Silent background polling for Untis users when no timetable exists.
     * This detects a backend auto-import (triggered by the login webhook
     * goroutine) without any visual feedback — the sync button stays visible.
     * When timetableExists becomes true, the component automatically switches
     * from EmptyTimetable to CalendarForDashboard.
     *
     * IMPORTANT: Only status.data is in the dependency array.
     * - status.loading is excluded to prevent interval teardown on each refetch
     * - No isPolling state is needed (polling is purely background, no UI effect)
     */
    useEffect(() => {
        // Stop polling when timetable exists or user is not from Untis
        if (!isUntisUser || status.data) {
            pollStartRef.current = null;
            return undefined;
        }
        // Don't start polling until initial GraphQL load is complete
        if (!initialLoadDoneRef.current) {
            return undefined;
        }
        pollStartRef.current = Date.now();
        const interval = setInterval(() => {
            if (pollStartRef.current &&
                Date.now() - pollStartRef.current > POLL_TIMEOUT_MS) {
                clearInterval(interval);
            }
            else {
                void refetch();
            }
        }, POLL_INTERVAL_MS);
        return () => {
            clearInterval(interval);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- status.loading excluded intentionally (see comment above)
    }, [isUntisUser, status.data, refetch]);
    const handleImportComplete = useCallback(() => {
        void refetch();
    }, [refetch]);
    /*
     * Show LoadingWrapper only for the initial GraphQL load.
     * After that, EmptyTimetable handles its own UI (sync button / spinner).
     */
    if (!initialLoadDoneRef.current) {
        return (_jsx(Card, { "data-testid": "Timetable", children: _jsx(CardContent, { sx: { flexGrow: 1 }, children: _jsx(LoadingWrapper, { status: status, children: _jsx(Stack, { alignItems: "center", justifyContent: "center", sx: { py: 8 }, children: _jsx(CircularProgress, {}) }) }) }) }));
    }
    return (_jsx(Card, { "data-testid": "Timetable", children: _jsx(CardContent, { sx: { flexGrow: 1 }, children: status.data ? (_jsx(CalendarForDashboard, { autoRefresh: isUntisUser })) : (_jsx(EmptyTimetable, { onImportComplete: handleImportComplete })) }) }));
};
export default Timetable;
